/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useContext, useState, useCallback, useId } from "react";
import styled from "styled-components";
import Icon from "./Icon";
import { useTranslate } from "../../hooks/useTranslate";

const StyledAccordion = styled.div`
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
`;

const StyledAccordionTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;

  ${(props) => props.$isOpen && `border-bottom: 1px solid rgba(${props.theme.colors.border.rgba});`}
`;

const StyledAccordionTitleChevron = styled((props) => <Icon.ChevronDown {...props} />)`
  stroke: rgba(${({ theme }) => theme.colors.text.rgb});
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const StyledAccordionToggleButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  display: flex;
  cursor: pointer;
  flex-shrink: 0;
`;

const StyledAccordionContent = styled.div`
  padding: 16px;
`;

const AccordionContext = createContext();

const AccordionProvider = ({ children, contentId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleAccordion = useCallback(() => {
    setIsOpen((prevState) => !prevState);
  }, []);

  return <AccordionContext.Provider value={{ isOpen, toggleAccordion, contentId }}>{children}</AccordionContext.Provider>;
};

const useAccordion = () => {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error("Accordion components must be used within an AccordionProvider");
  }
  return context;
};

const AccordionTitle = ({ children }) => {
  const { isOpen, toggleAccordion, contentId } = useAccordion();
  const { _t } = useTranslate();

  const handleToggleClick = (e) => {
    e.stopPropagation();
    toggleAccordion();
  };

  return (
    <StyledAccordionTitle $isOpen={isOpen} onClick={toggleAccordion}>
      {children}
      <StyledAccordionToggleButton
        type="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={isOpen ? contentId : undefined}
        aria-label={_t(isOpen ? "Hide details" : "Show details")}
        onClick={handleToggleClick}
      >
        <StyledAccordionTitleChevron size={16} $isOpen={isOpen} />
      </StyledAccordionToggleButton>
    </StyledAccordionTitle>
  );
};

const AccordionContent = ({ children }) => {
  const { isOpen, contentId } = useAccordion();
  return isOpen && <StyledAccordionContent id={contentId}>{children}</StyledAccordionContent>;
};

const Accordion = ({ children, isOpen: controlledIsOpen, onToggle, className }) => {
  const contentId = useId();

  if (onToggle !== undefined) {
    return (
      <AccordionContext.Provider value={{ isOpen: controlledIsOpen, toggleAccordion: onToggle, contentId }}>
        <StyledAccordion className={className}>{children}</StyledAccordion>
      </AccordionContext.Provider>
    );
  }
  return (
    <AccordionProvider contentId={contentId}>
      <StyledAccordion className={className}>{children}</StyledAccordion>
    </AccordionProvider>
  );
};

export { Accordion, AccordionTitle, AccordionContent };
